import { Component, output, input, model } from '@angular/core';
import { CheckboxComponent } from '@cue/assist/ui/inputs';
import { cueCaretRight, cueCaretDown, IconComponent } from '@cue/ui/icons';
import { LocalizedStringPipe } from '@cue/utilities';
import { RadioButtonComponent } from '@progress/kendo-angular-inputs';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { NgClass, NgTemplateOutlet } from '@angular/common';

export interface TreeItem {
  open: boolean;
  key: number;
  name: string;
  selected: boolean;
  children: TreeItem[];
}

@Component({
  selector: 'assist-area-tree',
  templateUrl: './area-tree.component.html',
  styleUrls: ['./area-tree.component.scss'],
  imports: [CheckboxComponent, LocalizedStringPipe, IconComponent, RadioButtonComponent, NgClass, NgTemplateOutlet],
})
export class AreaTreeComponent {
  single = input(false);

  selectionChanged = output<any[]>();

  ICONS = {
    cueCaretRight,
    cueCaretDown,
  };

  _finalData = [];
  data = input<any[]>();

  setData = explicitEffect([this.data], ([data]) => {
    this._finalData = this.buildTree(data);
  });

  openAll = input<boolean>();
  setOpenAll = explicitEffect([this.openAll], ([openAll]) => {
    if (openAll && this._finalData) {
      this._finalData = this.buildTree(this._finalData);
    }
  });

  key = 'id';
  name = 'name';

  setSelection(selection: any[], value: any) {
    value.selected = selection.includes(value[this.key]);
    value.children.forEach((child) => this.setSelection(selection, child));
  }

  selection = model<any[]>([]);
  setSelectionFromInput = explicitEffect([this.selection], ([selection]) => {
    if (this.data().length > 0) {
      this.setSelection(selection, this._finalData[0]);
    }
  });

  childSelected(level: TreeItem, toplevel = true): boolean {
    if (level.selected) return false;
    const selectedFromChildren = level.children.some((x) => x.selected);
    if (selectedFromChildren) return true;

    return level.children.some((x) => this.childSelected(x));
  }

  buildTree(value: any[]): TreeItem[] {
    const result = [];
    value.forEach((val) => {
      const newLevel: TreeItem = {
        selected: this.selection().includes(val[this.key]),
        [this.key]: val[this.key],
        key: val[this.key],
        name: val[this.name],
        open: this.openAll(),
        children: this.buildTree(val.children),
      };
      result.push(newLevel);
    });
    return result;
  }

  onSingleSelect(level: TreeItem) {
    this.selectionChanged.emit([level[this.key]]);
  }

  onSelect(level: TreeItem, newValue: boolean, top: boolean = true) {
    if (newValue === true) {
      if (!this.selection().includes(level.key)) {
        this.selection.set([...this.selection(), level.key]);
      }
    } else {
      const foundIndex = this.selection().indexOf(level.key);
      if (foundIndex !== -1) {
        this.selection.set([...this.selection().filter((x) => x != level.key)]);
      }
    }
    level.selected = newValue;
    level.children.forEach((ch) => this.onSelect(ch, newValue, false));

    if (top) this.selectionChanged.emit(this.selection());
  }
}
